import CloseIcon from '@mui/icons-material/Close'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import Skeleton from '@mui/material/Skeleton'
import React, { useState } from 'react'

interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  photographer?: string
  caption?: string | React.ReactNode
  skeletonHeight?: number | string
  clickable?: boolean
}

const Image: React.FC<ImageProps> = ({
  photographer,
  caption,
  skeletonHeight = 200,
  clickable = false,
  onLoad,
  loading = 'lazy',
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setIsLoaded(true)
    if (onLoad) onLoad(e)
  }

  const toggleFullscreen = () => {
    if (isLoaded && clickable) {
      setIsOpen(!isOpen)
    }
  }

  return (
    <>
      <figure
        style={{
          margin: 0, // Ändrat från 1em till 0 för att stoppa overflow och sidoscroll
          width: props.width || '100%',
          position: 'relative',
          boxSizing: 'border-box',
        }}
      >
        {!isLoaded && (
          <Skeleton
            variant="rectangular"
            width="100%"
            height={skeletonHeight}
            sx={{ borderRadius: '10px' }}
          />
        )}

        <img
          {...props}
          loading={loading}
          width="100%"
          onLoad={handleLoad}
          onClick={toggleFullscreen}
          style={{
            borderRadius: 10,
            display: 'block',
            width: '100%',
            height: 'auto', // Säkerställer att bilden behåller sina korrekta proportioner
            opacity: isLoaded ? 1 : 0,
            position: isLoaded ? 'static' : 'absolute',
            top: 0,
            left: 0,
            transition: 'opacity 0.3s ease-in-out',
            cursor: isLoaded && clickable ? 'pointer' : 'default',
            boxSizing: 'border-box',
            ...props.style,
          }}
        />

        {isLoaded && (caption || photographer) && (
          <figcaption
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '0.85rem',
              color: '#555',
              marginTop: 6,
              padding: '0 4px',
            }}
          >
            <span>{caption}</span>
            {photographer && (
              <span
                style={{
                  fontStyle: 'italic',
                  color: '#888',
                  marginLeft: '10px',
                }}
              >
                Foto: {photographer}
              </span>
            )}
          </figcaption>
        )}
      </figure>

      <Dialog
        open={isOpen}
        onClose={toggleFullscreen}
        maxWidth="xl"
        PaperProps={{
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            boxShadow: 'none',
            overflow: 'hidden',
            margin: 16,
            position: 'relative',
          },
        }}
      >
        <IconButton
          onClick={toggleFullscreen}
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            color: '#fff',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>

        <img
          src={props.src}
          alt={props.alt}
          style={{
            maxWidth: '100vw',
            maxHeight: '90vh',
            objectFit: 'contain',
            display: 'block',
            margin: 'auto',
          }}
        />

        {(caption || photographer) && (
          <div
            style={{
              color: '#fff',
              padding: '12px 20px',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              fontSize: '0.9rem',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <span>{caption}</span>
            {photographer && (
              <span style={{ fontStyle: 'italic', color: '#ccc' }}>
                Foto: {photographer}
              </span>
            )}
          </div>
        )}
      </Dialog>
    </>
  )
}

export default Image
